'use client';

import Input from 'src/components/primitives/Input/Input';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { useCreateReferralLink } from 'src/hooks/driftActions/user/useCreateReferralLink';
import { useEffect, useState } from 'react';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useDebounce } from 'use-debounce';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useDriftClient from 'src/hooks/useDriftClient';
import useReferrerNameAccounts from 'src/hooks/useReferrerNameAccounts';
import { referralLinkBannedCharactersRegex } from 'src/constants/constants';
// @ts-ignore
import { regex as badwordsRegex } from 'badwords-list';
import { decodeName, encodeName } from '@drift-labs/sdk';
import useModal from 'src/components/Modals/hooks/useModal';
import ResponsiveModal from 'src/components/Modals/ResponsiveModal/ResponsiveModal';
import Button from 'src/components/primitives/Button/Button';

const MAX_CHARS = 32;

const CreateReferralLinkModal = () => {
	const { fetchReferrerNameAccounts } = useReferrerNameAccounts();
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();
	const { createReferralLink } = useCreateReferralLink();
	const authority = useDriftAccountStore((s) => s.accounts[0]?.authority);
	const accountName = useDriftAccountStore((s) => s.accounts[0]?.name);
	const { hide } = useModal();

	const [submitting, setSubmitting] = useState(false);
	const [referrerNameAlreadyUsed, setReferrerNameAlreadyUsed] = useState(false);
	const [referrerName, setReferrerName] = useState<string>(
		(accountName === 'Main Account'
			? authority?.toString()?.slice(0, MAX_CHARS)
			: accountName?.replace(referralLinkBannedCharactersRegex, '')) ?? ''
	);

	const hasBannedCharacters =
		!!referrerName.match(referralLinkBannedCharactersRegex) ||
		decodeName(encodeName(referrerName)) !== referrerName;
	const hasBadWords = !!referrerName.match(badwordsRegex);
	const buttonDisabled =
		referrerName.length === 0 ||
		referrerName.length > MAX_CHARS ||
		referrerNameAlreadyUsed ||
		hasBannedCharacters ||
		hasBadWords ||
		submitting;

	const handleEditReferrerName = async () => {
		setSubmitting(true);

		const setReferrerNameResult = await createReferralLink(referrerName);

		if (setReferrerNameResult === true) {
			// This is not working without a timeout and I don't know why.
			// The await above isn't actually waiting or TX to approve maybe?
			// I don't really get why.
			setTimeout(() => {
				fetchReferrerNameAccounts();
				hide();
			}, 2000);
		} else {
			// wat do?
			setSubmitting(false);
		}
	};

	const handleValueChange = (value: string) => {
		let cleanValue = value
			.replace(referralLinkBannedCharactersRegex, '')
			.toLowerCase();

		if (cleanValue.length > MAX_CHARS) {
			cleanValue = cleanValue.slice(0, MAX_CHARS);
		}

		setReferrerName(cleanValue);
	};

	const checkIfNameIsInUse = async (nameTocheck: string) => {
		try {
			const existingAccount =
				await driftClient?.fetchReferrerNameAccount(nameTocheck);
			if (existingAccount) {
				// In use?
				setReferrerNameAlreadyUsed(true);
			} else {
				// Not in use?
				setReferrerNameAlreadyUsed(false);
			}
		} catch (err) {
			// Not in use
			setReferrerNameAlreadyUsed(false);
		}
	};

	const [debouncedReferrerName] = useDebounce(referrerName, 1000);
	useEffect(() => {
		if (driftClientIsReady && !!debouncedReferrerName) {
			checkIfNameIsInUse(debouncedReferrerName);
		}
	}, [debouncedReferrerName, driftClientIsReady]);

	return (
		<ResponsiveModal.Container
			onClose={hide}
			dataTestId="create-referral-link-modal"
		>
			<ResponsiveModal.Title closeButtonTestId="create-referral-link-modal-close">
				Customize Your Referral Link
			</ResponsiveModal.Title>
			<ResponsiveModal.Body>
				<p>You can only have one referral link per Drift account.</p>

				<p>
					Your referral link cannot be changed. Also, note that once a user
					signs up using this referral code, the original subaccount for this
					wallet can no longer be deleted.
				</p>

				<Input
					id="referrer-name"
					type="text"
					labelVariant="standard"
					label="Your Referral Link"
					value={referrerName}
					onChange={handleValueChange}
				/>

				<Input
					id="refferal-link"
					type="text"
					labelVariant="standard"
					label="Link Preview"
					value={`app.drift.trade/ref/${referrerName}`}
					disabled
				/>

				{referrerNameAlreadyUsed && (
					<InlineAlert
						variant="error"
						title="This custom name has already been taken. Please choose a different name."
					/>
				)}

				{hasBannedCharacters && (
					<InlineAlert
						variant="error"
						title="Referral link has invalid characters"
					/>
				)}

				{hasBadWords && (
					<InlineAlert
						variant="error"
						title="Referral link cannot include banned words"
					/>
				)}
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				<Button
					size="lg"
					className="w-full"
					disabled={buttonDisabled}
					onClick={handleEditReferrerName}
					isLoading={submitting}
					dataTestId="create-referral-link-modal-submit"
				>
					Create
				</Button>
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
};

export default CreateReferralLinkModal;
